import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, closeChromeThenNavigate, openOverlayScrim } from "../helpers/overlay_chrome"
import { rollDigits } from "../helpers/digit_roll"

// The bank-review deck (budgets/_bank_review_deck) and, in the same shell,
// the first-import welcome (budgets/_bank_welcome_deck).
//
// The review deck opens on demand from the bank-review strip
// (bank-review-deck:open) and shows the rows a bank sync brought in, in one
// scrolling list. Nothing in it acts on a row: looking is the action.
// Dismiss — the CTA, or a tap on the scrim — submits the deck's form
// (PlaidController#reviewed), which stamps the rows, banks the day's streak
// action and refreshes the dashboard; the streak +1 card plays on that
// render.
//
// The welcome opens by itself (autoValue) once the dashboard has settled and
// carries no form: the import that raised it already banked the day, so
// Let's go only re-fetches the dashboard, where the +1 card is waiting.
//
// Opening and the native chrome are due_deck_controller's, cut down: the same
// scrim-matched dim, the same stage / mascot / reveal entrance, the same
// turbo:before-cache reset. The submit goes through closeChromeThenNavigate so
// the undim reaches the native side before the page that raised it is gone
// (overlay_chrome rule 1).
export default class extends Controller {
  static targets = ["stage", "mascot", "reveal", "card", "roll", "form"]
  static values = { auto: Boolean }

  static SHOW_DELAY = 600
  static EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"
  static EASE_SPRING = "cubic-bezier(0.2, 1.55, 0.35, 1)"

  connect() {
    this._busy = false
    this._timers = []
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    // Same beat as the Due deck, and never over a card another controller
    // has already raised for this render.
    if (this.autoValue) {
      this._at(this.constructor.SHOW_DELAY, () => {
        if (!openOverlayScrim()) this.open()
      })
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._clearTimers()
    this._unlockScroll()
  }

  open() {
    if (!this.element.hidden) return

    this._busy = false
    this.element.hidden = false
    this._lockScroll()
    openChrome(this, this.element)
    // Same double-rAF re-assert as due_deck_controller: the budget carousel
    // shows the tab bar for its first slide during load.
    requestAnimationFrame(() => requestAnimationFrame(() => openChrome(this, this.element)))
    this._entrance()
  }

  // One post, however many taps land while it is in flight.
  //
  // Posted with fetch, then the dashboard is re-fetched with a Turbo replace
  // visit — due_deck_controller#close's way — rather than submitted as a
  // form: a form submit answers with a native refresh, which blanks the web
  // view while it reloads. A Turbo visit keeps this page on screen until the
  // next one has arrived, so the bubble leaves, the scrim stays, and the
  // streak +1 card's own scrim takes over on the render that follows. There
  // is never a frame of bare dashboard.
  dismiss() {
    if (this._busy) return

    this._busy = true
    const exit = this._exit()
    const settled = exit ? exit.finished.catch(() => {}) : Promise.resolve()
    const request = this.hasFormTarget ? this._post() : Promise.resolve(true)

    Promise.all([request, settled]).then(([ok]) => {
      if (!ok) {
        // Refused or never landed (offline, 5xx): close locally, the way the
        // Due deck closes, so the dashboard is usable again. The strip keeps
        // its rows, which is the server's truth, and the next tap tries again.
        this._closeLocally(exit)
        return
      }

      this._unlockScroll()
      document.dispatchEvent(new CustomEvent("celebration:closed"))
      // The welcome's Let's go leaves the tab bar down: the render it fetches
      // rebuilds the bar anyway, and showing it here first flashed it up,
      // gone, and up again before the streak +1 card took it back down.
      closeChromeThenNavigate(this, () => window.Turbo.visit(window.location.href, { action: "replace" }),
        { showBottomNav: !this.autoValue })
    })
  }

  _closeLocally(exit) {
    exit?.cancel()
    this.element.hidden = true
    this._unlockScroll()
    this._busy = false
    document.dispatchEvent(new CustomEvent("celebration:closed"))
    closeChrome(this)
  }

  _exit() {
    if (this._reducedMotion) return null

    return this.stageTarget.animate(
      [{ transform: "scale(1)", opacity: 1 }, { transform: "scale(0.94)", opacity: 0 }],
      { duration: 240, easing: "cubic-bezier(0.32, 0, 0.3, 1)", fill: "forwards" }
    )
  }

  // The deck's own form supplies the token and the budget; it is never
  // submitted as a form (see dismiss). Resolves to whether the server took it.
  _post() {
    const form = this.formTarget
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(form.action, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      body: new FormData(form)
    }).then((response) => response.ok, () => false)
  }

  _entrance() {
    if (this._reducedMotion) {
      this.mascotTarget.style.cssText = "opacity: 1; transform: none"
      this.revealTargets.forEach((el) => { el.style.opacity = "1" })
      this.cardTargets.forEach((el) => { el.style.opacity = "1" })
      return
    }

    this.stageTarget.animate(
      [{ transform: "scale(0.9)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }],
      { duration: 550, delay: 120, easing: "cubic-bezier(0.2, 1.05, 0.3, 1)", fill: "both" }
    )
    this.mascotTarget.animate(
      [{ transform: "translateY(30px) scale(0.7)", opacity: 0 }, { transform: "translateY(0) scale(1)", opacity: 1 }],
      { duration: 620, delay: 420, easing: this.constructor.EASE_SPRING, fill: "both" }
    )
    this.revealTargets.forEach((el, i) => {
      el.animate(
        [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 460, delay: 620 + i * 70, easing: this.constructor.EASE_OUT, fill: "both" }
      )
    })
    // The day cards follow the list in, one after another, from the moment
    // the list's own reveal starts (third reveal target: 620 + 2 * 70).
    this.cardTargets.forEach((el, i) => {
      el.animate(
        [{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 420, delay: 760 + i * 60, easing: this.constructor.EASE_OUT, fill: "both" }
      )
    })
    // The welcome's counts roll in once their card has landed, like the
    // budget card's figures (helpers/digit_roll).
    this.rollTargets.forEach((el, i) => {
      this._at(980 + i * 90, () => rollDigits(el, true, { restore: true }))
    })
  }

  // turbo:before-cache: hidden and unlocked, so a restored snapshot never
  // carries an open deck.
  _reset() {
    this._clearTimers()
    this.element.hidden = true
    this._unlockScroll()
  }

  _at(ms, fn) {
    this._timers.push(setTimeout(fn, ms))
  }

  _clearTimers() {
    this._timers.forEach((t) => clearTimeout(t))
    this._timers = []
  }

  get _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  // Only the lock this deck took is released (see review_prompt_controller).
  _unlockScroll() {
    if (this._prevOverflow === undefined) return

    document.documentElement.style.overflow = this._prevOverflow
    this._prevOverflow = undefined
  }
}
